import React from 'react';
import { Link } from 'react-router-dom';
import { ProfileStats as ProfileStatsType } from '../../types/profile';
import { useTranslation } from 'react-i18next';

interface ProfileStatsProps {
    stats: ProfileStatsType;
    isOwnProfile: boolean;
}

const ProfileStats: React.FC<ProfileStatsProps> = ({ stats, isOwnProfile }) => {
    const { t } = useTranslation('profile');

    const hasAnyActivity =
        stats.coursesEnrolled > 0 ||
        stats.totalLearningTime > 0 ||
        stats.coursesCreated > 0;

    if (!hasAnyActivity) {
        if (isOwnProfile) {
            return (
                <div className="profile-stats profile-stats--empty">
                    <h2 className="profile-stats__title">{t('stats.title', 'Learning Stats')}</h2>
                    <p className="profile-stats__empty-title">
                        {t('humanEmpty.statsTitle', 'No learning history yet')}
                    </p>
                    <p className="profile-stats__empty-body">
                        {t('humanEmpty.statsBody', 'Start a course and your progress will appear here.')}
                    </p>
                    <Link to="/courses" className="profile-stats__cta">
                        {t('humanEmpty.browseCourses', 'Browse courses →')}
                    </Link>
                </div>
            );
        }
        return null;
    }

    const formatLearningTime = (seconds: number): string => {
        const minutes = Math.floor(seconds / 60);
        if (minutes < 60) {
            return `${minutes}m`;
        }
        const hours = Math.floor(minutes / 60);
        const mins = minutes % 60;
        if (mins === 0) return `${hours}h`;
        return `${hours}h ${mins}m`;
    };

    const items: Array<{ label: string; value: string | number }> = [];

    if (stats.coursesCreated > 0) {
        items.push({
            label: t('stats.coursesCreated', 'Courses Created'),
            value: stats.coursesCreated,
        });
    }
    if (stats.coursesEnrolled > 0) {
        items.push({
            label: t('stats.coursesEnrolled', 'Courses Enrolled'),
            value: stats.coursesEnrolled,
        });
    }
    if (stats.totalLearningTime > 0) {
        items.push({
            label: t('stats.learningTime', 'Learning Time'),
            value: formatLearningTime(stats.totalLearningTime),
        });
    }

    return (
        <div className="profile-stats">
            <h2 className="profile-stats__title">{t('stats.title', 'Learning Stats')}</h2>
            <dl className="profile-stats__list">
                {items.map((item, index) => (
                    <div key={index} className="profile-stats__item">
                        <dt className="profile-stats__label">{item.label}</dt>
                        <dd className="profile-stats__value">{item.value}</dd>
                    </div>
                ))}
            </dl>
        </div>
    );
};

export default ProfileStats;
